import { dateLocale } from "@/lib/dateTime";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const DOW = ["M", "T", "W", "T", "F", "S", "S"];

/** A small month for jumping around: the shown range is tinted and today is filled */
export function MiniMonth({ focus, rangeStart, rangeEnd, onPick }: { focus: Date; rangeStart: Date; rangeEnd: Date; onPick: (d: Date) => void }) {
  const [month, setMonth] = useState(new Date(focus.getFullYear(), focus.getMonth(), 1));
  useEffect(() => setMonth(new Date(focus.getFullYear(), focus.getMonth(), 1)), [focus]);

  const first = new Date(month);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
  while (cells.length % 7) cells.push(null);
  const today = new Date().toDateString();
  const inRange = (d: Date) => d >= rangeStart && d < rangeEnd;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between px-1">
        <span className="text-[13px] font-semibold">{month.toLocaleDateString(dateLocale(), { month: "long", year: "numeric" })}</span>
        <span className="flex gap-0.5">
          <button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-muted">
            <ChevronLeft className="size-4" />
          </button>
          <button type="button" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-muted">
            <ChevronRight className="size-4" />
          </button>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[11.5px]">
        {DOW.map((d, i) => (
          <span key={i} className="text-[10.5px] leading-[22px] text-muted-foreground">
            {d}
          </span>
        ))}
        {cells.map((d, i) =>
          d ? (
            <button
              key={i}
              type="button"
              onClick={() => onPick(d)}
              className={cn(
                "h-[26px] rounded-md font-mono",
                d.toDateString() === today ? "bg-primary font-bold text-primary-foreground" : inRange(d) ? "bg-accent text-foreground" : "text-foreground hover:bg-muted",
              )}
            >
              {d.getDate()}
            </button>
          ) : (
            <span key={i} />
          ),
        )}
      </div>
    </div>
  );
}
